/**
 * CustomizeFieldsDialog — field selection + section order for DSU report.
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { GripVertical } from 'lucide-react';
import { REPORT_FIELDS } from '../../hooks/reports/reportConstants';

const SECTION_LABELS = {
  vetting: 'Vetting Procedures',
  views:   'Views',
};

const SectionOrderList = ({ sectionOrder, setSectionOrder }) => {
  const [dragIndex, setDragIndex] = useState(null);

  const handleDragStart = (i) => setDragIndex(i);
  const handleDragOver  = (e, i) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === i) return;
    const updated = [...sectionOrder];
    const [moved] = updated.splice(dragIndex, 1);
    updated.splice(i, 0, moved);
    setSectionOrder(updated);
    setDragIndex(i);
  };
  const handleDragEnd = () => setDragIndex(null);

  return (
    <div>
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
        Section Order <span className="normal-case font-normal">(drag to reorder)</span>
      </p>
      <div className="space-y-1">
        {sectionOrder.map((key, i) => (
          <div
            key={key}
            draggable
            onDragStart={() => handleDragStart(i)}
            onDragOver={(e) => handleDragOver(e, i)}
            onDragEnd={handleDragEnd}
            className={`flex items-center gap-3 p-2 rounded border cursor-grab select-none transition-colors
              ${dragIndex === i ? 'border-primary bg-primary/5' : 'border-transparent hover:bg-muted/50'}`}
          >
            <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="text-sm flex-1">{SECTION_LABELS[key]}</span>
            <Badge variant="outline" className="text-[9px] pointer-events-none">
              {i === 0 ? 'First' : 'Second'}
            </Badge>
          </div>
        ))}
      </div>
    </div>
  );
};

const CustomizeFieldsDialog = ({
  open, onOpenChange, activeReport,
  selectedFields, toggleField, saveFieldPrefs,
  sectionOrder, setSectionOrder,
  filters, setFilters,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Customize Report Fields</DialogTitle>
        <p className="text-sm text-muted-foreground">Select which fields to include and arrange section order.</p>
      </DialogHeader>
      <div className="space-y-4 py-2">
        {/* Date picker — DSU only, at the top of the dialog */}
        {activeReport === 'daily-sale-updates' && filters && setFilters && (
          <div className="flex items-center gap-3 pb-2 border-b">
            <Label className="text-sm shrink-0">Report Date</Label>
            <Input
              type="date"
              value={filters.date || ''}
              onChange={e => setFilters(f => ({ ...f, date: e.target.value }))}
              className={`w-40 h-8 text-sm ${filters.date ? 'date-filled' : ''}`}
              data-testid="dsu-date-picker"
            />
            {filters.date && (
              <button
                className="text-xs text-muted-foreground hover:text-foreground"
                onClick={() => setFilters(f => ({ ...f, date: '' }))}
                title="Reset to today"
              >✕ Today</button>
            )}
            {!filters.date && (
              <span className="text-xs text-muted-foreground">Defaults to today</span>
            )}
          </div>
        )}
        {/* Section order — DSU only */}
        {activeReport === 'daily-sale-updates' && sectionOrder && setSectionOrder && (
          <>
            <SectionOrderList sectionOrder={sectionOrder} setSectionOrder={setSectionOrder} />
            <hr />
          </>
        )}

        {/* Field selection */}
        {Object.entries(REPORT_FIELDS[activeReport] || {}).map(([group, fields]) => (
          <div key={group}>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{group}</p>
            <div className="space-y-1">
              {fields.map(f => (
                <label key={f.key} className="flex items-center gap-3 p-2 rounded hover:bg-muted/50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedFields.includes(f.key)}
                    onChange={() => toggleField(f.key)}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <span className="text-sm flex-1">{f.label}</span>
                  {f.default && <Badge variant="outline" className="text-[9px] pointer-events-none">Default</Badge>}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      <DialogFooter>
        <Button variant="outline" size="sm" onClick={() => {
          const defaults = Object.values(REPORT_FIELDS[activeReport] || {}).flat().filter(f => f.default).map(f => f.key);
          saveFieldPrefs(defaults);
          if (setSectionOrder) setSectionOrder(['vetting', 'views']);
        }}>Reset to Defaults</Button>
        <Button onClick={() => onOpenChange(false)} className="bg-primary hover:bg-primary/90 text-white">Done</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default CustomizeFieldsDialog;
